import React, { Component } from 'react';
import { Prompt } from 'react-router-dom';
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, styled } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import { FLOW_METRICS_INTERVAL_LIFETIME } from 'js/constants/metricsConstants';
import {
  FLOW_TYPE_CUSTOM,
  PIPELINE_ACTION_ACTIVATE,
  PIPELINE_ACTION_DELETE,
  PIPELINE_ACTION_MOVE_TO_PROJECT,
  PIPELINE_ACTION_PAUSE,
} from 'js/constants/pipelineConstants';

import { Button } from 'new/elements/support/Button';
import TypographySupport from 'new/elements/TypographySupport';
import { useFlowDeletionStatus } from 'new/hooks/adapters/useFlowDeletionStatus';
import { FlowActionButton } from 'new/elements/Button';
import { FLOW_RUN_AFFORDANCE_ACTIVATE_WITH_CHOICE } from 'new/utils/flowRunAffordance';
import FlowPreviewContainer from '../sendData/summaryViews/FlowPreviewContainer';

import { muiStyles, styles } from './ModalPipelineAction.styles';

const StyledDialogTitle = styled(DialogTitle)(muiStyles.dialogTitle);
const StyledDialogContent = styled(DialogContent)(muiStyles.dialogContent);

class PipelineDialogContent extends Component {
  _getTitle = () => {
    const { pipelineAction } = this.props;

    switch (pipelineAction && pipelineAction.pipelineActionType) {
      case PIPELINE_ACTION_ACTIVATE:
        return 'Activate Flow';

      case PIPELINE_ACTION_PAUSE:
        return 'Pause Flow';

      case PIPELINE_ACTION_DELETE:
        return 'Delete Flow';

      case PIPELINE_ACTION_MOVE_TO_PROJECT:
        return 'Move Flow to a Project';

      default:
    }
  };

  _getDescription = () => {
    const { pipelineAction } = this.props;

    switch (pipelineAction && pipelineAction.pipelineActionType) {
      case PIPELINE_ACTION_ACTIVATE:
        return 'All resources shown below will be activated.';

      case PIPELINE_ACTION_PAUSE:
        return 'All resources shown below will be paused.';

      case PIPELINE_ACTION_DELETE:
        return 'This will permanently delete the selected item and everything downstream from it.';

      default:
    }
  };

  _getActionText = () => {
    const { pipelineAction } = this.props;

    switch (pipelineAction && pipelineAction.pipelineActionType) {
      case PIPELINE_ACTION_ACTIVATE:
        return 'Activate';

      case PIPELINE_ACTION_PAUSE:
        return 'Pause';

      case PIPELINE_ACTION_DELETE:
        return 'Delete';

      case PIPELINE_ACTION_MOVE_TO_PROJECT:
        return 'Move Flow';

      default:
        return 'Confirm';
    }
  };

  _renderCloseButton = (label = 'Close') => {
    const { onCancel } = this.props;
    return (
      <FlowActionButton actionType="cancel" onClick={onCancel} showIcon={false} label={label} />
    );
  };

  _renderDialogActions = () => {
    const {
      pipelineAction,
      onCancel,
      onConfirm,
      flowDeletionStatus,
      isPausing,
      isDeleting,
      isActivating,
      canChooseRunNow,
    } = this.props;

    const actionType = pipelineAction?.pipelineActionType;
    const canDelete = flowDeletionStatus?.canDelete;
    const reason = flowDeletionStatus?.reason;

    switch (actionType) {
      case PIPELINE_ACTION_DELETE:
        return (
          <>
            {this._renderCloseButton('Cancel')}

            {canDelete === false && (
              <FlowActionButton
                actionType="pause"
                isLoading={isPausing}
                onClick={() => onConfirm(PIPELINE_ACTION_PAUSE)}
              >
                Pause
              </FlowActionButton>
            )}

            {reason ? (
              <Tooltip title={reason} placement="top-start">
                <span>
                  <FlowActionButton
                    isLoading={isDeleting}
                    disabled={!canDelete}
                    actionType="delete"
                    onClick={() => onConfirm(PIPELINE_ACTION_DELETE)}
                  />
                </span>
              </Tooltip>
            ) : (
              <FlowActionButton
                disabled={!canDelete}
                actionType="delete"
                isLoading={isDeleting}
                onClick={() => onConfirm(PIPELINE_ACTION_DELETE)}
              />
            )}
          </>
        );

      case PIPELINE_ACTION_ACTIVATE:
        if (canChooseRunNow) {
          return (
            <>
              {this._renderCloseButton('Cancel')}
              <FlowActionButton
                actionType="activate"
                isLoading={isActivating}
                label="Activate & Run Now"
                onClick={() => onConfirm(PIPELINE_ACTION_ACTIVATE, true)}
              />
              <FlowActionButton
                actionType="activate"
                isLoading={isActivating}
                label="Activate for Next Cycle"
                onClick={() => onConfirm(PIPELINE_ACTION_ACTIVATE, false)}
              />
            </>
          );
        }

        // A single button means "this flow is ready — tell control". The choice is only offered
        // where the source has a recurring schedule for control to pick the flow up on; without
        // one, run_now=false would leave the flow ACTIVE and never started. run_now=true is
        // equivalent to omitting the param (admin-api data_source.rb:747 — only an explicit falsy
        // suppresses the control event) and matches the source card, where Activate is a single
        // button calling PUT /data_sources/{id}/activate with no params.
        return (
          <>
            {this._renderCloseButton('Cancel')}
            <FlowActionButton
              actionType="activate"
              isLoading={isActivating}
              onClick={() => onConfirm(PIPELINE_ACTION_ACTIVATE, true)}
            />
          </>
        );

      case PIPELINE_ACTION_PAUSE:
        return (
          <>
            {this._renderCloseButton('Cancel')}
            <FlowActionButton
              actionType="pause"
              isLoading={isPausing}
              onClick={() => onConfirm(PIPELINE_ACTION_PAUSE)}
            />
          </>
        );

      default:
        return (
          <>
            {this._renderCloseButton()}

            <Button
              data-testid="dialog-confirm-button"
              color="error"
              variant="contained"
              style={{ marginRight: 0 }}
              onClick={() => onConfirm(actionType)}
            >
              {this._getActionText()}
            </Button>
          </>
        );
    }
  };

  render() {
    const { pipelineAction, isLoading, onCancel, onConfirm, flowDeletionStatus, isCustomFlow } =
      this.props;

    const hasPipelineAction = pipelineAction?.pipelineActionType;

    return (
      <>
        <StyledDialogTitle>
          <TypographySupport variant="h3" style={styles.dialogHeader}>
            {this._getTitle()}
          </TypographySupport>
          <Divider light />
        </StyledDialogTitle>

        <StyledDialogContent dividers>
          <p style={styles.dialogContentText}>{this._getDescription()}</p>
          <FlowPreviewContainer
            nodeKey={pipelineAction?.flowNodeId}
            excludeUpstream
            excludeFlowControlNode={!isCustomFlow}
          />
          <Prompt when={!!pipelineAction} message={() => false} />
        </StyledDialogContent>

        <DialogActions>
          {hasPipelineAction && this._renderDialogActions()}
          {!hasPipelineAction && !isLoading && this._renderCloseButton()}
        </DialogActions>
      </>
    );
  }
}

class ModalPipelineAction extends React.Component {
  constructor(props) {
    super(props);
    this._handleCancelClicked = this._handleCancelClicked.bind(this);
    this._handleConfirmClicked = this._handleConfirmClicked.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const { flowNodes, isLoading, pipelineAction, loadPipeline, loadFlowMetrics, isPausing } =
      this.props;

    // load pipeline if not already loaded
    if (
      prevProps.pipelineAction !== pipelineAction &&
      pipelineAction?.flowNodeId &&
      !flowNodes[pipelineAction.flowNodeId]
    ) {
      loadPipeline(pipelineAction.flowNodeId);
    }

    /**
     * if the dataFlowSource was provided, load the metrics
     */
    if (flowNodes[pipelineAction?.flowNodeId]) {
      loadFlowMetrics(pipelineAction.flowNodeId, {
        interval: FLOW_METRICS_INTERVAL_LIFETIME,
      }).then(({ result }) => {
        if (result.status !== 200) {
          console.error('Error to reach out the metrics');
        }
      });
    }

    const isPauseAction = pipelineAction?.pipelineActionType === PIPELINE_ACTION_PAUSE;
    const isDeleteAction = pipelineAction?.pipelineActionType === PIPELINE_ACTION_DELETE;

    const wasPreviouslyPausing = prevProps.isPausing;
    const isCurrentDeleteAndPausing = isDeleteAction && isPausing;
    const shouldSkipAutoDismissModal = isPauseAction
      ? false
      : isCurrentDeleteAndPausing || wasPreviouslyPausing;

    /**
     * if the action is deleted and the request is pausing, don't close the modal otherwise, close the modal
     */
    if (prevProps.isLoading && !isLoading && !shouldSkipAutoDismissModal) {
      this._handleCancelClicked();
    }
  }

  render() {
    const {
      pipelineAction,
      flowRunAffordance,
      isLoading,
      loadPipelines,
      isPausing,
      isDeleting,
      isActivating,
    } = this.props;

    const flowNodeId = pipelineAction?.flowNodeId;
    const canChooseRunNow =
      pipelineAction?.pipelineActionType === PIPELINE_ACTION_ACTIVATE &&
      flowRunAffordance === FLOW_RUN_AFFORDANCE_ACTIVATE_WITH_CHOICE;
    const flowDeletionStatus =
      flowNodeId && this.props.getFlowDeletionStatus
        ? this.props.getFlowDeletionStatus(flowNodeId)
        : null;
    const currentFlow = this.props.flowNodes?.[flowNodeId];
    const isCustomFlow = currentFlow?.flow_type === FLOW_TYPE_CUSTOM;
    const isDeleteAction = pipelineAction?.pipelineActionType === PIPELINE_ACTION_DELETE;

    return (
      <Dialog
        fullWidth
        maxWidth={'lg'}
        open={!!pipelineAction}
        onClose={this._handleCancelClicked}
        data-testid={isDeleteAction ? 'delete-flow-modal' : undefined}
      >
        <PipelineDialogContent
          isLoading={isLoading}
          isPausing={isPausing}
          isDeleting={isDeleting}
          isActivating={isActivating}
          isCustomFlow={isCustomFlow}
          canChooseRunNow={canChooseRunNow}
          pipelineAction={pipelineAction}
          loadPipelines={loadPipelines}
          onCancel={this._handleCancelClicked}
          onConfirm={this._handleConfirmClicked}
          flowDeletionStatus={flowDeletionStatus}
        />
      </Dialog>
    );
  }

  _handleConfirmClicked(overrideActionType = null, runNow = false) {
    const { pipelineAction, pausePipeline, activatePipeline, deletePipeline } = this.props;

    const actionType = overrideActionType || pipelineAction?.pipelineActionType;

    switch (actionType) {
      case PIPELINE_ACTION_ACTIVATE:
        activatePipeline(pipelineAction.flowNodeId, runNow);
        break;

      case PIPELINE_ACTION_PAUSE:
        pausePipeline(pipelineAction.flowNodeId);
        break;

      case PIPELINE_ACTION_DELETE: {
        const deletePromise = deletePipeline(pipelineAction.flowNodeId);
        if (typeof this.props.onDeleteSuccess === 'function') {
          deletePromise.then(() => this.props.onDeleteSuccess(pipelineAction.flowNodeId));
        }
        break;
      }

      default:
    }
  }

  _handleCancelClicked() {
    this.props.setPipelineAction();
  }
}

const ModalPipelineActionWrapper = (props) => {
  const { getFlowDeletionStatus } = useFlowDeletionStatus();

  return <ModalPipelineAction {...props} getFlowDeletionStatus={getFlowDeletionStatus} />;
};

export default ModalPipelineActionWrapper;
